<template>
  <v-expansion-panel class="exp-panel-table" :class="panelColor ? panelColor + '-panel' : ''">
    <v-expansion-panel-header class="py-2 px-8" v-if="!isScoreHeader">
      <v-row justify="space-between" align="center">
        <h1 class="font-weight-bold mb-0 mr-3" :class="countColor ? countColor + '--text' : 'primary--text'">
          {{ totalData }}
        </h1>
        <v-col>
          <v-row>
            <p class="mb-0 font-weight-bold">{{ headerTextFirstRow }}</p>
          </v-row>
          <v-row>
            <p class="mb-0">{{ headerTextSecondRow }}</p>
          </v-row>
        </v-col>
        <v-col v-if="headerIcon" class="text-right">
          <v-icon large :color="headerIconColor ? headerIconColor : ''">{{ headerIcon }}</v-icon>
        </v-col>
      </v-row>
    </v-expansion-panel-header>
    <v-expansion-panel-header v-else>
      <v-row justify="space-between" align="center">
        <RadialScore ref="score" :score="headerScoreObject" :score-icon="headerScoreIcon" :chart-width="headerScoreWidth"/>
        <v-col>
          <v-row>
            <p class="mb-0 font-weight-bold">{{ headerTextFirstRow }}</p>
          </v-row>
          <v-row>
            <p class="mb-0">{{ headerTextSecondRow }}</p>
          </v-row>
        </v-col>
      </v-row>
    </v-expansion-panel-header>

    <v-expansion-panel-content class="mt-1" eager>
      <v-data-table
          :headers="headers"
          :items="data"
          :hide-default-footer="!showTableDefaultFooter"
          :footer-props="{itemsPerPageText: $t('components:datatable.itemsPerPage') ,
                                    itemsPerPageAllText: $t('common:all') }"
          :options.sync="options"
          :server-items-length="totalData"
          :item-class="rowColor"
          :no-data-text="$t('common:no-data-found')"
          :no-results-text="$t('components:datatable.noResults')"
          dense
          @click:row="clickedRow"
      >
        <!-- Additional checkboxes that can be set to specific properties -->
        <template v-slot:[`item.checkbox`]="{ item }">
          <v-icon>
            {{ checkShowCheckbox(checkboxOnProperty, item) ? "check_box" : "check_box_outline_blank" }}
          </v-icon>
        </template>

        <template v-slot:[`item.checkbox2`]="{ item }">
          <v-icon>
            {{ checkShowCheckbox(checkboxOnProperty2, item) ? "check_box" : "check_box_outline_blank" }}
          </v-icon>
        </template>

        <template v-slot:[`item.checkbox3`]="{ item }">
          <v-icon>
            {{ checkShowCheckbox(checkboxOnProperty3, item) ? "check_box" : "check_box_outline_blank" }}
          </v-icon>
        </template>

        <template v-slot:[`item.asButton`]="{ item }">
          <v-btn
              text
              small
              class="no-text-transform text-decoration-underline"
              @click.native.stop="$emit('clickedAsButton1', item)"
          >
            {{ item[asButtonProperty] }}
          </v-btn>
        </template>

        <!-- Additional buttons that can emit functions -->
        <template v-slot:[`item.controls`]="{ item }">
          <v-tooltip bottom :disabled="!iconTooltip">
            <template v-slot:activator="{ on, attrs}">
              <v-btn v-if="checkShowButton(icon, dependsOnProperty, item)" icon :color="iconColor ? iconColor : 'primary'"
                     @click.native.stop="clickedButton1(item)" v-bind="attrs" v-on="on">
                <v-icon>{{ icon }}</v-icon>
              </v-btn>
            </template>
            <span>{{ iconTooltip }}</span>
          </v-tooltip>

          <!-- Event for the second button -->
          <v-tooltip bottom :disabled="!iconTooltip2">
            <template v-slot:activator="{ on, attrs}">
              <v-btn v-if="checkShowButton(icon2, dependsOnProperty2, item)" icon :color="iconColor2 ? iconColor2 : 'primary'"
                     @click.native.stop="clickedButton2(item)" v-bind="attrs" v-on="on">
                <v-icon>{{ icon2 }}</v-icon>
              </v-btn>
            </template>
            <span>{{ iconTooltip2 }}</span>
          </v-tooltip>

          <!-- Event for the third button -->
          <v-tooltip bottom :disabled="!iconTooltip3">
            <template v-slot:activator="{ on, attrs}">
              <v-btn v-if="checkShowButton(icon3, dependsOnProperty3, item)" icon :color="iconColor3 ? iconColor3 : 'primary'"
                     @click.native.stop="clickedButton3(item)" v-bind="attrs" v-on="on">
                <v-icon>{{ icon3 }}</v-icon>
              </v-btn>
            </template>
            <span>{{ iconTooltip3 }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-expansion-panel-content>
    <v-expansion-panel-content class="mt-1 btn-panel" v-if="!hideToggleAllBtn">
      <v-row>
        <v-col class="text-center">
          <v-btn
              text
              color="primary"
              class="text-center font-weight-bold"
              @click="toggleAllData"
          >
            {{ footerBtnText }}
          </v-btn>
        </v-col>
      </v-row>

    </v-expansion-panel-content>
  </v-expansion-panel>
</template>
<script>
import RadialScore from "@/modules/core/components/RadialScore";
export default {
  name: 'ExpansionPanelDataTablePaginated',
  components: {RadialScore},
  props: {
    countColor: String,

    headerTextFirstRow: String,
    headerTextSecondRow: String,
    headerIcon: String,
    headerIconColor: String,

    isScoreHeader: Boolean,
    headerScoreObject: Object,
    headerScoreIcon: String,
    headerScoreWidth: String,

    footerBtnText: String,
    rowThemeColor: String,

    // v-data-table props
    headers: Array, //header information
    panelColor: String,

    // v-data-table tempalte props
    icon: String, //icon for the first button, if not given, button is not shown
    icon2: String, //^ second button
    icon3: String, //^ third button

    iconColor: String,
    iconColor2: String,
    iconColor3: String,

    iconTooltip: String,
    iconTooltip2: String,
    iconTooltip3: String,

    dependsOnProperty: String, //will only show first button if row property with this value is set to true
    dependsOnProperty2: String, //^ second button
    dependsOnProperty3: String, //^ third button

    checkboxOnProperty: String, //if set, will allow to show a checkbox depending on the given property
    checkboxOnProperty2: String, //^ second checkbox
    checkboxOnProperty3: String, //^ third checkbox,

    sortBy: String,
    sortDesc: Boolean,

    disableRowClick: Boolean, // disable being able to click on row + event
    showTableDefaultFooter: Boolean, // show the default footer of the dataTable component
    hideToggleAllBtn: Boolean, // hides the toggle all data btn

    asButtonProperty: String // field of asButtonField
  },
  data() {
    return {
      options: {
        sortBy: [ "name" ],
        sortDesc: [ false ],
        itemsPerPage: 5,
        page: 1,
      },
      data: [],
      totalData: 0,
      searchTimer: null,
      tableUpdateTimer: null
    }
  },
  mounted() {
    if(this.sortBy) {
      this.options.sortBy[0] = this.sortBy;
    }
    if(this.sortDesc != this.options.sortDesc[0]) {
      this.options.sortDesc[0] = this.sortDesc;
    }
  },
  watch: {
    //by deep watching the table options, we can automatically call an update whenever sort/page changes
    options: {
      handler() {
        this.callTableUpdate();
      },
      deep: true,
      immediate: true //with this it will trigger on initialization -> we don't have to load on mount
    },
    headerScoreObject() {
      this.$refs.score.score = this.headerScoreObject;
      this.$refs.score.scoreIcon = this.headerScoreIcon;
    }
  },
  methods: {
    toggleAllData() {
      this.$emit("toggleAllData");
    },

    rowColor(item) {
      if (item.isEven) {
        if (this.disableRowClick || this.data.length === 0) {
          return this.rowThemeColor + '-1 table-row--disabled';
        } else {
          return this.rowThemeColor + '-1';
        }
      } else {
        if (this.disableRowClick || this.data.length === 0) {
          return this.rowThemeColor + '-2 table-row--disabled';
        } else {
          return this.rowThemeColor + '-2';
        }
      }
    },
    /**
     * Method to send out for updating the table data.
     * Loading the correct data depends on the parent component, so we just pass an event to the parent.
     */
    callTableUpdate(){
      //using tiny timer to catch overlapping update calls made by the watch-functions
      clearTimeout(this.tableUpdateTimer);
      this.tableUpdateTimer = setTimeout( () => {
        this.$emit('updateTableData', this.options);
      },50);
    },
    /**
     * Method for passing loaded data back into the table.
     *
     * @param data paginated data to pass into the table
     */
    fillTableData(data){
      if (data == null) {
        this.data = [];
      } else {
        this.data = data.content;

        for (let i = 0; i < this.data.length; i++) {
          if (i % 2 == 0) {
            this.data[i].isEven = true;
          } else {
            this.data[i].isEven = false;
          }
        }
      }
      this.totalData = data.totalElements;
    },
    clickedButton1(item) {
      this.$emit('clickedButton1', item);
    },
    clickedButton2(item) {
      this.$emit('clickedButton2', item);
    },
    clickedButton3(item) {
      this.$emit('clickedButton3', item);
    },
    clickedAddButton(item) {
      this.$emit('clickedAddButton', item);
    },
    checkShowButton(icon, property, item) {
      if (icon == null) {
        return false;
      }
      if (property == null) {
        return true;
      }
      //check if the item in the given row has the required property and return it
      if (Object.prototype.hasOwnProperty.call(item, property)) {
        return item[property];
      }
      return false;
    },
    checkShowCheckbox(property, item) {
      if (property == null) {
        return false;
      }
      //check if the item in the given row has the required property and return it
      if (Object.prototype.hasOwnProperty.call(item, property)) {
        return item[property];
      }
      return false;
    },
    clickedRow(item) {
      this.$emit('clickedRow', item);
    }
  }
}
</script>
<style lang="scss">
.exp-panel-table {
  /* Available row colors */
  .scaleRed-1 {
    background-color: #fbd2d2;

    td:first-child {
      color: #ec2929;
      font-weight: bold;
      letter-spacing: 1px;
    }
  }
  .scaleRed-1:hover {
    background-color: #e6c0c0!important;
  }
  .scaleRed-2 {
    background-color: #fde8e8;
    td:first-child {
      color: #ec2929;
      font-weight: bold;
      letter-spacing: 1px;
    }
  }
  .scaleRed-2:hover {
    background-color: #e6c0c0!important;
  }

  .grey-1 {
    background-color: rgba(0, 0, 0, 0.15);
  }
  .grey-2 {
    background-color: rgba(0, 0, 0, 0)
  }

  .v-expansion-panel-content__wrap {
    padding: 0!important;
  }

  tr:not(.table-row--disabled):not(.v-data-table__empty-wrapper) {
    cursor: pointer;
  }

  // for default tables, make the border lines white
  tr > td {
    border-right: thin solid white!important;
    border-bottom: thin solid white!important;
  }

  // underline "show all ..." btn
  .btn-panel {
    button {
      text-decoration: underline;
    }
  }


  tr > th {
    font-size: 0.8rem!important;
    color: var(--v-greyTint-base)!important;
    letter-spacing: 0.1rem;

    .v-data-table-header__icon {
      color: var(--v-greyTint-base)!important;
    }
  }

  // override hovering color for rows for default table
  tr > th.active {
    color: var(--v-greyMedium-base)!important;

    .v-data-table-header__icon {
      color: var(--v-greyMedium-base)!important;
    }
  }
}


// css style for panel-table with color primary
.exp-panel-table.primary-panel {
  .primary-1 {
    background-color: #33b9ed;
    color: white;

    td {
      border-right: thin solid #1CB1EBFF!important;
      border-bottom: thin solid #1CB1EBFF!important;
    }
  }

  .primary-1:hover {
    background-color: #1591c2!important;
  }

  .primary-2 {
    background-color: #1CB1EBFF;
    color: white;

    td {
      border-right: thin solid #1CB1EBFF!important;
      border-bottom: thin solid #1CB1EBFF!important;
    }
  }

  .primary-2:hover {
    background-color: #1591c2!important;
  }

  .v-expansion-panel-content__wrap {
    padding: 0!important;
  }

  // make panel header primary with white text
  .v-expansion-panel-header {
    background-color: var(--v-primary-base)!important;
    color: white;

    .v-expansion-panel-header__icon {
      .v-icon {
        color: white!important;
      }
    }
  }

  .v-expansion-panel-content {
    background-color: var(--v-primary-base) !important;
    color: white!important;

    thead {
      background-color: var(--v-primary-base) !important;

      tr > th {
        color: white!important;
        border-bottom: none!important;

        .v-icon {
          color: white!important;
        }
      }
    }

    tbody {
      background-color: #33b9ed !important;
      tr > td {
        color: white!important;
      }
    }

    button {
      color: white!important;
    }

    .v-data-footer {
      margin-top: 4px;
      border-top: none;
      background-color: #33b9ed !important;

      .v-data-footer__select {
        color: white;

        .theme--light.v-text-field > .v-input__control > .v-input__slot:before {
          border-color: white!important;
        }

        .v-select__selection.v-select__selection--comma{
          color: white!important;
        }

        .v-input__icon.v-input__icon--append {
          .v-icon {
            color: white!important;
          }
        }
      }

      .v-data-footer__pagination {
        color: white;
      }
    }
  }
}
</style>